import { generateSetChild, generateAddArrayChild, generateUpdateArrayChild, generateRemoveArrayChild, generateSetMapChild } from '@/helpers/VuexHelpers'
import { service } from '@/service'
import cookie from 'cookie_js'
import { config } from '@/config'
import { dayjs, dayjsServer } from '@/helpers/dayjs'
import { fixProduct, enhanceProducts, filterProductsByGroup } from '@/service/upgradeService'
import { i18n } from '@/app/vuewidget'
import { getUrlParams} from '@/utils/urlParams';
import tipVariants from './tipVariants'
import { getRandomIndex } from '@/utils/random'

let cartPromise = null

export default function orderTemplate({ config, storeType = 'order', useCookie = false, orderEditable = false }) {
	return {
		state: {
			isDetached: false,
			isFeedback: false,
			items: [],
			initialLoad: false,
			productMap: {},
			basketToken: null,
			basketChannel: "",
			serverData: null,
			loading: false,
			openRequests: 0,
			vouchers: [],
			tip: null,
			co2Compensation: false,
			surcharges: [],
			deliveryType: 0,
			changedItems: null,
			updatedAt: 0,
			showContinue: true,
			crossSell: [],
			tipTypes: [
				'none',
				'auto',
				'percent',
				'chf'
			],
			tipType: 'none',
			tipPercent: 0,
			tipOption: null,
			tipVariants,
			tipMainVariant: 0,
			tipSubVariant: 0,
		},
		actions: {
			setIsFeedback({ commit }, value) {
				commit('setIsFeedback', { item: value })
			},
			async load({commit, dispatch, state, rootState}, { token = null, recalculate = false, accumulatorGroup = null } = {}) {
				if (cartPromise) {
					return cartPromise
				}
				//get a new cart or use existing one
				commit('setLoading', { item: true })
				commit('setOpenRequests', { item: state.openRequests + 1 })

				if (useCookie) {
					await dispatch('restoreCart', { token })
				} else {
					state.basketToken = token
				}

				//check for the basket token
				if (state.basketToken == null) {
					throw new Error("no basket token")
				}

				cartPromise = service.cartOrOrder({ token: state.basketToken, recalculate, accumulatorGroup })
				let cart = await cartPromise
					.catch(err => Promise.resolve({error: true, request: err}))
				cartPromise = null

				// if we're in the cart store and checkout state is completed, but payment is awaiting, restore order
				if (storeType == 'cart' && cart.checkoutState == 'completed' && ['awaiting_payment'].includes(cart.paymentState)) {
					const { cancel, error } = getUrlParams();
					const basketToken = cookie.get(config.CART_COOKIE);
					const cartToken = cart.tokenValue;

					try {
						if (cancel || error) {
							cart = await dispatch('restoreOrder', { token: state.basketToken });
							cartPromise = service.cartOrOrder({ token: cart.tokenValue, recalculate, accumulatorGroup })
							cart = await cartPromise
								.catch(err => Promise.resolve({error: true, request: err}))
							cartPromise = null
						} else {
							dispatch('clearCart');
							commit('setOpenRequests', { item: state.openRequests - 1 })
							commit('setLoading', { item: false })
							return false;
						}
					}
					catch(err) {
						config.DEBUG && console.error(err);
						cart = { error: true }
					}
					finally {
						// only remove cookie if it matches the current token
						if (token == basketToken || cartToken == basketToken) {
							cookie.removeSpecific(config.CART_COOKIE, { path: "/" })
						}
					}
				}
				// if we're in the cart store and checkout state is completed, treat cart as order
				else if (storeType == 'cart' && cart.checkoutState == 'completed') {
					commit('setBasketToken', { item: null })
					commit('setIsDetached', { item: false })
					if (rootState.shopArea !== 'checkout-success') {
						return await dispatch('redirect', { name: 'checkout-complete', params: { basketToken: cart.tokenValue }}, { root: true })
					}
					await dispatch('order/readCart', { cart }, { root: true })
					commit('setOpenRequests', { item: state.openRequests - 1 })
					commit('setLoading', { item: false })
					const basketToken = cookie.get(config.CART_COOKIE);

					// only remove cookie if it matches the current token
					if (token == basketToken || cart.tokenValue == basketToken) {
						cookie.removeSpecific(config.CART_COOKIE, { path: "/" })
					}
					return false;
				}

				if (cart.error) {
					//cart could not be loaded
					if (cart.request && cart.request.response.statusCode == 404) {
						//basket token was not found
						const basketToken = cookie.get(config.CART_COOKIE);

						// only remove cookie if it matches the current token
						if (token == basketToken) {
							cookie.removeSpecific(config.CART_COOKIE, { path: "/" })
						}
						commit('setBasketToken', { item: null })
					}
					commit('setOpenRequests', { item: state.openRequests - 1 })
					commit('setLoading', { item: false })
					return false
				} else if (cart) {
					commit('setOpenRequests', { item: state.openRequests - 1 })
					await dispatch('readCart', { cart })
					commit('setLoading', { item: false })

					// eslint-disable-next-line require-atomic-updates
					state.initialLoad = true
				}
				return true
			},

			async readCart({commit, state, dispatch, rootGetters}, { cart, ignoreDetached = false } = {}) {
				// TODO: early implementation of deffered cart display
				if (state.openRequests > 0) {
					return
				}
				if (cart) {
					if (typeof cart.tokenValue !== "string") {
						config.DEBUG && console.error("cart.readCart: Invalid cart Token", cart)
						throw new Error("Invalid cart Token")
					}
					if (useCookie) {
						(state.isDetached && !ignoreDetached) || cookie.set(config.CART_COOKIE, cart.tokenValue, { path: "/" })
					}

					let cartItems = cart.items
					commit('setBasketToken', { item: cart.tokenValue })
					commit('setBasketChannel', { item: cart.channel.code })
					cart.items = null // reduce load for state
					commit('setData', { item: cart })

					const stateItems = [...state.items]; // this is necessary, because otherwise the loop below will skip items when deleting
					stateItems.forEach(item => {
						let exist = cartItems.find(v => v.id == item.id)
						if (!exist) {
							commit("removeItem", { item })
						}
					})

					// we have to collect additional product codes to check if its a product
					let additionalProductCodes = []
					if (config.FEATURE_ADDITIONALPRODUCTS && cart.availableAdditionalProducts) {
						additionalProductCodes = cart.availableAdditionalProducts
							.reduce((prev, rule) => {
								return prev.concat(
									rule.products.map(product => typeof product === 'object' ? product.code: product)
								)
							}, [])
					}

					// read co2
					commit('setCo2Compensation', { item: !!cart.compensateCo2 })

					// read tip
					if (cart.totals.tip) {
						commit('setTip', { item: { total: cart.totals.tip } })
					}

					// read referenced products
					if (cart.products && cart.products.length > 0) {
						commit('setProducts' ,{ items: cart.products.map(p => fixProduct(p)) })
					}

					// child products
					let childProductsMap = {}
					if (cart.availableChildProducts && cart.menu) {
						childProductsMap = cart.availableChildProducts.reduce((p, c) => {
							// commit('setProduct' ,{ item: c })
							if (typeof c !== 'object') {
								c = state.productMap[c]
							}
							const taxon = cart.childProductsCategories && cart.childProductsCategories.find(cat => cat.taxon == c.taxons.main || cat.taxon == c.taxons.others?.[0]?.taxon);
							p[c.code] = {
								code: c.code,
								name: c.variants[0].name,
								description: c.description,
								total: c.variants[0].price,
								image: c.images,
								taxons: c.taxons.others,
								type: taxon ? taxon.type : 'extra',
							}
							return p
						}, {})
					}

					if (config.FEATURE_GROUP_ORDERS && cart.isGroupOrder) {
						if (storeType == 'cart' && !cart.groupOrderParticipantId) {
							dispatch('grouporder/clearGroupOrder', {}, { root: true });
							dispatch('loader/setLoading', false, { root: true });
							await dispatch('dialog/showDialog', {
								title: i18n.t('cart.groupOrder.removed.dialogTitle'),
								message: i18n.t('cart.groupOrder.removed.dialogText'),
								confirm: i18n.t('cart.groupOrder.removed.dialogConfirm'),
							}, { root: true });
							dispatch('redirect', { name: 'home' }, { root: true });
							return;
						}
						else {
							dispatch('grouporder/setParticipantId', { id: cart.groupOrderParticipantId }, { root: true })
							dispatch('grouporder/setParticipants', { participants: cart.groupOrderParticipants }, { root: true })

							if (storeType == 'cart'&& cart.firebaseConfig) {
								dispatch('grouporder/startFirebaseService', { firebaseConfig: cart.firebaseConfig }, { root: true })
							}

							if (storeType == 'cart' && !rootGetters['grouporder/groupOrderIsOwner']) {
								if (cart.isGroupOrderLocked) {
									dispatch('dialog/showDialog', {
										title: i18n.t('cart.groupOrder.locked.dialogTitle'),
										message: i18n.t('cart.groupOrder.locked.dialogText'),
										// confirm: i18n.t('cart.groupOrder.locked.dialogConfirm'),
									}, { root: true });
								}
								else if (!cart.isGroupOrderLocked && rootGetters['grouporder/groupOrderIsLocked']) {
									dispatch('dialog/showDialog', {
										title: i18n.t('cart.groupOrder.unlocked.dialogTitle'),
										message: i18n.t('cart.groupOrder.unlocked.dialogText'),
										confirm: i18n.t('cart.groupOrder.unlocked.dialogConfirm'),
									}, { root: true });
								}
							}
							dispatch('grouporder/setGroupOrderLock', { locked: cart.isGroupOrderLocked }, { root: true })
						}
					}

					if (cart.crossSell) {
						const crossSellProducts = cart.crossSell.map(product => {
							if (typeof product !== 'object') {
								product = state.productMap[product]
							}

							return product;
						});

						commit('setCrossSell', { item: crossSellProducts });
					}

					cartItems.forEach(item => {
						let exist = state.items.find(v => v.id == item.id)

						if (typeof item.product !== 'object') {
							item.product = state.productMap[item.product]
						} else {
              item.product = fixProduct(item.product)
            }

						// DEPRECATED: this is now a surcharge and should no longer exist in th cart
						// handle tip and strip it out of the service
						if (item.product.isTip) {
							commit('setTip', { item: { total: item.total } })
							return
						}

						const product = item.product

						let total = item.total
						let variant = item.variantCode != null ? product.variants.find(v => v.code === item.variantCode): product.variants[0]
						let children = [];
						let co2 = {
							amount: variant.co2.amount,
							emission: variant.co2.emission,
						};
						let nutrient = {
							calories: variant.nutrient.calories,
						};

						if (item.children) {
							// child products
							children = item.children.map(child => {
								const product = typeof child.product === 'object' ? child.product : state.productMap[child.product]
								const variant = product.variants.length > 1 ? product.variants.find(v => child.variantCode) : product.variants[0]

								if (variant.co2 && variant.co2.emission) {
									co2.emission += variant.co2.emission;
								}
								if (variant.co2 && variant.co2.amount) {
									co2.amount += variant.co2.amount;
								}
								if (variant.nutrient && variant.nutrient.calories) {
									nutrient.calories += variant.nutrient.calories;
								}

								return {
									name: product.name,
									code: product.code,
									price: variant.price,
									taxons: childProductsMap[product.code] ? childProductsMap[product.code].taxons : [],
									type: childProductsMap[product.code] ? childProductsMap[product.code].type : 'extra',
								}
							});
						}

						let newItem = {
							id: item.id,
							code: product.code,
							name: product.name,
							displayName: (() => {
								let name = product.name;
								if (cart.orderType == 'reusable_dishes' && item.detailedName) {
									return item.detailedName
								} else if (variant.name && !product.name.includes(variant.name)) {
									if (product.name === variant.name || variant.name.includes(product.name)) {
										name = variant.name;
									}
									else {
										name += ` ${variant.name}`;
									}
								}
								return name
							})(),
							detailedName: cart.orderType == 'reusable_dishes' && item.detailedName,
							description: product.description,
							quantity: item.quantity,
							taxons: product.taxons,
							dishEnabled: product.dishEnabled,
							disableNote: product.disableNote ?? true,
							total,
							notes: item.notes || '',
							//server sends another total value for cartdisplay
							// only use total if originalTotal is undefined
							originalTotal: item.originalTotal !== undefined ? item.originalTotal : total,
							//HACK: variant handling as default???
							price: variant.price,
							image: product.image,
							imageSpecial: product.imageSpecial,
							imageMood: product.imageMood,
							imagesDetail: product.imagesDetail,
							variant: product.name != variant.name ? variant.name : null,
							variantCode: product.code != variant.code ? variant.code : product.code,
							availableChildProducts: product.availableChildProducts || [],
							defaultChildProducts: product.defaultChildProducts || [],
							averageRatings: product.averageRatings,

							// child products
							children,

							// co2 and nutrient total
							co2,
							nutrient,

							//debug
							isVariant: variant.code != product.code,
							isAdditional: product.isAdditionalProduct,
              isAdditionalAllowed: product.isAdditionalProductAllowed,
							isEditable: product.isEditable || false, // TODO: first draft of childproducts
							isConfigurable: product.isConfigurable || false, // TODO: first draft of childproducts
							isTip: product.isTip || false,
							isNew: true, // this is a way to detect if an item was just added now
							groupOrderParticipantId: item.groupOrderParticipantId,
						}
						if (!exist) {
							commit('addItem', { item: newItem })
						} else {
							newItem.isNew = false // on item update we set this to false
							commit('updateItem', { item: newItem })
						}
					})

					if (cart.notAvailableItems) {
						const changedItems = cart.notAvailableItems
							.map(v => ({
								id: v.id,
								name: v.product.name,
								image: v.product.images.length && v.product.images[0].path || "",
							}))
						commit('setChangedItems', { item: changedItems })
					}

					if (cart.tipOption != null && !state.isFeedback) {
						commit('setTipOption', { item: cart.tipOption })
					}

					if (cart.tipMainVariant != null) {
						commit('setTipMainVariant', { item: cart.tipMainVariant })
					}

					if (cart.tipSubVariant != null) {
						commit('setTipSubVariant', { item: cart.tipSubVariant })
					}

					if (orderEditable || state.isFeedback) {
						dispatch('calculateTip');
					}
					commit('setUpdatedAt', { item: Date.now() })
				}
			},

			async restoreOrder({ commit, dispatch, state }, { token }) {
				const params = { token, channel: state.basketChannel, keepOrder: true };

				if (state.basketToken != null && state.basketToken != token) {
					params.oldToken = state.basketToken;
				}

				let result = await service.orderRestore(params)
				commit('setBasketToken', { item: result.tokenValue })
				if (result.notAvailableItems) {
					const changedItems = result.notAvailableItems
						.map(v => ({
							id: v.id,
							name: v.product.name,
							image: v.product.images.length && v.product.images[0].path || "",
						}))
					commit('setChangedItems', { item: changedItems })
				}
				return result
			},

			adjustTip({ commit }, { value }) {
				if (value) {
					commit('setTipType', { item: 'chf' })
				}
				commit('setTip', { item: { total: value } })
			},

			setTipOption({ commit, dispatch }, { value }) {
				commit('setTipOption', { item: value })
				dispatch('calculateTip', { reset: true });
			},

			setTipType({ commit, dispatch }, { value }) {
				commit('setTipType', { item: value })
				dispatch('calculateTip', { reset: true });
			},

			setTipPercent({ state, commit, dispatch }, { value }) {
				commit('setTipPercent', { item: value })
				dispatch('calculateTip');
			},

			calculateTip({ state, commit, getters }, { reset = false } = {}) {
				if (reset) {
					commit('setTip', { item: 0 });
					commit('setTipPercent', { item: 0 });
				}
				if (state.tipType !== 'chf' || state.tipPercent) {
					let tip = 0;
					let total = getters.totalWithoutSurcharges;
					if (state.tipType == 'auto' && total) {
						tip = total >= 30 ? total * 0.05 : total * 0.10;
					}
					if (['percent', 'chf'].includes(state.tipType) && total) {
						tip = total / 100 * state.tipPercent;
					}
					tip = Math.round(tip / 5) * 5; // round to 5 cents
					commit('setTip', { item: { total: tip } });
				}
			},

			randomizeTipVariant({ state, commit }) {
				if (config.FEATURE_TIP_VARIANTS) {
					let mainVariantIndex = state.serverData?.tipMainVariant ?? window.localStorage.getItem('fwgtmv')
					let subVariantIndex = state.serverData?.tipSubVariant ?? window.localStorage.getItem('fwgtsv')

					if (mainVariantIndex == null || subVariantIndex == null) {
						mainVariantIndex = getRandomIndex(state.tipVariants)
						const mainVariant = state.tipVariants[mainVariantIndex]
						subVariantIndex = getRandomIndex(mainVariant.subVariants)

						window.localStorage.setItem('fwgtmv', mainVariantIndex)
						window.localStorage.setItem('fwgtsv', subVariantIndex)
					}

					commit('setTipMainVariant', { item: +mainVariantIndex })
					commit('setTipSubVariant', { item: +subVariantIndex })
				}
			},

			clearTipVariant({ commit }) {
				window.localStorage.removeItem('fwgtmv')
				window.localStorage.removeItem('fwgtsv')
			}
		},
		getters: {
			items(state) {
				return state.items
			},
			itemsWithoutAdditional (state, getters, rootState, rootGetters) {
				const childProducts = []
				return state.items
					.filter(p => p.isAdditional != true)
					.map(item => {
            item.children = item.children.map(child => {
              const category = rootGetters['childproducts/childProductCategories'].find(cat => cat.products?.includes(child.code))
              return {
                ...child,
                type: category?.childType ?? 'extra'
              }
            })
						childProducts.push(...item.children)
						return item
					})
					.filter(item => !childProducts.some(child => child.code === item.code))
			},
			additionalItems(state, getters, rootState, rootGetters) {
				return state.items
					.filter(item => item.isAdditional == true)
					//HACK: this works only if we join 2 items
					.reduce((prev, item, i, items) => {
						let sameProduct = items.slice(i + 1).find(other => item.code == other.code)
						if (sameProduct != null && sameProduct != item && item.hide != true) {
							let newItem = {
								...item,
								total: item.total + sameProduct.total,
								originalTotal: item.originalTotal + sameProduct.originalTotal,
								quantity: item.quantity + sameProduct.quantity,
								origIds: [item.id, sameProduct.id]
							}
							sameProduct.hide = true
							prev.push(newItem)
						} else if (item.hide) {
							//do nothing
						} else {
							prev.push(item)
						}
						return prev
					}, [])
			},
			additionalItemsPrice(state, getters) {
				return getters.additionalItems
					.reduce((p, c) => p + c.originalTotal, 0)
			},
			totalWithoutSurcharges(state, getters) {
				let total = 0
				if (state.serverData) {
					const {
						totals: { total: serviceTotal, tip, co2Compensation },
					} = state.serverData

					total = serviceTotal
										- (tip || 0)
										- (co2Compensation || 0)
				}
				return total
			},
			totalWithoutTip(state, getters) {
				let total = getters.totalWithoutSurcharges;

				if (state.serverData) {
					const {
						totals: { co2Compensation },
					} = state.serverData

					if (orderEditable) {
						total += (getters.cartChannelHasCo2Compensation && getters.cartCo2Compensation ? getters.cartCo2CompensationAmount : 0)
					} else {
						total += co2Compensation
					}
				}

				return total;
			},
			totalWithoutCo2(state, getters) {
				if (orderEditable) {
					return getters.totalWithoutSurcharges
									+ (getters.cartTipEnabled || getters.orderStates.tipState == 'locked' && getters.orderStates.tipState !== 'disabled' ? getters.cartTip : 0)
				} else {
					return state.serverData?.totals ? state.serverData.totals.total - state.serverData.totals.co2Compensation : 0
				}
			},
			total(state, getters) {
				if (orderEditable) {
					return getters.totalWithoutTip
									+ (getters.cartTipEnabled || getters.orderStates.tipState == 'locked' && getters.orderStates.tipState !== 'disabled' ? getters.cartTip : 0)
				} else {
					return state.serverData?.totals.total || 0
				}
			},
			subtotal(state, getters) {
				return getters.items.reduce((total, item) => total + item.originalTotal, 0);
			},
			totalItems(state) {
				return state.items
					.reduce((p, c) => p + c.quantity, 0)
			},
			// TODO: find a better solution for this
			totalItemsWithoutAdditional(state) {
				return state.items
					.reduce((p, c) => c.isAdditional ? p : p + c.quantity, 0)
			},
			totalCo2(state) {
				return state.items.reduce((total, item) => {
					total.emission += +(item.co2?.emission || 0);
					total.amount += +(item.co2?.amount || 0);
					return total;
				}, {emission: 0, amount: 0});
			},
			totalNutrient(state) {
				return state.items.reduce((total, item) => {
					total.calories += +(item.nutrient?.calories || 0);
					return total;
				}, {calories: 0});
			},
			hasBasket(state) {
				return !!state.serverData
			},
			basketState(state) {
				return state.serverData
					? state.serverData.checkoutState
					: "none"
			},
			discounts(state) {
				return state.serverData
					? Object.entries(state.serverData.cartDiscounts).map(([code,v]) => ({...v, code}))
					: []
			},
			discountCode(state) {
				return state.serverData
					? state.serverData.couponCode
					: null
			},
			deliveryPrice(state) {
				return state.serverData
					? state.serverData.totals.shipping
					: 0
			},
			deliveryTime(state) {
				//in minutes
				return state.serverData
					? state.serverData.zip
						? state.serverData.zip.deliveryTime
						: 0
					: 0
			},
			minCartPrice(state) {
				return state.serverData
					? state.serverData.zip
						? state.serverData.zip.minDeliveryAmount * 100
						: 0
					: 0
			},
			minCartFreePrice(state) {
				return state.serverData
				? state.serverData.zip?.minBasketFree
					? state.serverData.zip.minBasketFree * 100
					: 0
				: 0
			},
			shippingMethod(state) {
				return state.serverData
					? state.serverData.shipments[0].method
					: null
			},
			payments(state) {
				return state.serverData?.payments || []
			},
			shopLocation(state) {
				return state.serverData
					? state.serverData.channel
					: null
			},
			channelName(state) {
				return state.serverData
					? state.serverData.channel?.name || state.serverData.channelName
					: null
			},
			cartIncludesAlcohol(state) {
				return state.serverData
					? state.serverData.alcoholCheck == true
					: false
			},
			cartDeliveryTime(state) {
				return state.serverData
					? dayjsServer(state.serverData.deliverTime)
					: null
			},
			cartTip (state, getters) {
				if (orderEditable || state.isFeedback) {
					if (state.tip) {
						return state.tip.total
					}
					return getters.cartTipDefault
				} else {
					return state.serverData?.totals.tip || 0
				}
			},
			cartTipEnabled (state) {
				if (orderEditable || state.isFeedback) {
					return state.serverData
						? (state.serverData.channel.location.tipEnabled || false)
						: null
				} else {
					return state.serverData ? state.serverData.totals.tip > 0 : 0
				}
			},
			cartTipDefault (state) {
				return state.serverData?.channel?.location?.defaultTip ?? 0
			},
			cartTipTypes(state) {
				return state.tipTypes;
			},
			cartTipType(state) {
				return state.tipType;
			},
			cartTipPercent(state) {
				return state.tipPercent;
			},
			cartTipVariant(state, getters) {
				const variant = state.tipVariants?.[state.tipMainVariant]?.subVariants?.[state.tipSubVariant]

        const parseOption = (option) => {
          return {
            ...option,
            label: option.label
              || (option.labelKey && i18n.t(option.labelKey))
              || option.labelFn?.(option, getters)
              || '',
            valueModified: option.modifier?.(option, getters)
          }
        }

				if (variant) {
					return {
            ...variant,
            title: variant.title
              || (variant.titleKey && i18n.t(variant.titleKey))
              || variant.titleFn?.(variant, getters)
              || null,
            hint: variant.hint
              || (variant.hintKey && i18n.t(variant.hintKey))
              || variant.hintFn?.(variant, getters)
              || null,
            options: variant.options.map(option => {
              return {
                ...parseOption(option),
                options: option.options?.map(o => parseOption(o)),
              }
            })
          }
				}

				return null
			},
			cartTipOption(state) {
				return state.tipOption;
			},
			cartChannelHasCo2Compensation (state) {
				return state.serverData
					? state.serverData.channel.location.hasCo2Compensation
					: false
			},
			cartCo2Compensation (state) {
				return state.co2Compensation
			},
			cartCo2CompensationAmount (state) {
				return state.serverData
					? state.serverData.channel?.location?.co2CompensationAmount || state.serverData.totals?.co2Compensation
					: 0
			},
			cartCo2CompensationRatio (state) {
				return state.serverData
					? state.serverData.channel?.location?.co2CompensationRatio
					: 0
			},
			cartChangedItems(state) {
				// TODO: currently its possible that the cartitem was removed and so the reference is broken
				return state.changedItems
					?	state.changedItems
							.map(v => ({
								...v,
								item: state.items.find(item => item.id == v.id)
							}))
					: []
			},
			cartOrderNow(state) {
				return state.serverData
					? state.serverData.orderNow
					: false
			},
			cartOrderNowAvailable(state) {
				return state.serverData
					? state.serverData.orderNowAvailable
					: false
			},
			cartUpdateAt(state) {
				return state.updatedAt
			},
			cartLoading(state) {
				return state.loading
			},
			cartOrderTimePassed(state, getters) {
				return !['locked', 'disabled'].includes(getters.orderStates.timeState) ? getters.cartOrderNow == true && getters.cartOrderNowAvailable == false : false
			},
			cartBigOrderText(state) {
				return state.serverData
					? state.serverData.bigOrderText
					: null;
			},
			showContinue(state) {
				return !!state.showContinue;
			},
			cartIsDetached(state) {
				return !!state.isDetached;
			},
			cartChannelHasDishesEnabled (state) {
				return state.serverData
					? state.serverData.channel.dishesEnabled
					: false
			},
			cartHasItemsWithDishEnabled (state, getters) {
				return state.items.some(v => v.dishEnabled)
			},
			tokenValue(state) {
				return state.basketToken
			},
			orderType (state) {
				return state.serverData?.orderType || 'main';
			},
			orderStates (state) {
				return {
					paymentState: state.serverData?.paymentState || 'new',
					checkoutState: state.serverData?.checkoutState || 'locked',
					tipState: state.serverData?.tipState || 'locked',
					co2State: state.serverData?.co2State || 'locked',
					shippingAddressState: state.serverData?.shippingAddressState || 'locked',
					billingAddressState: state.serverData?.billingAddressState || 'locked',
					timeState: state.serverData?.timeState || 'locked',
					reusableDishState: state.serverData?.reusableDishState || 'locked',
					promotionCouponState: state.serverData?.promotionCouponState || 'locked',
					dropoffState: state.serverData?.dropoffState || 'locked',
					notesState: state.serverData?.notesState || 'locked',
					isAnonymous: state.serverData?.isAnonymous || false,
				}
			},
			cartCrossSell(state, getters, rootState, rootGetters) {
				return enhanceProducts(state.crossSell || [], rootGetters)
					.filter(filterProductsByGroup(rootGetters))
			},
			cartOutdoorDeliveryPoint(state) {
				return state.serverData?.outdoorDeliveryPoint || null
			},
			cartIsGroupOrder(state) {
				return !!state.serverData?.isGroupOrder
			},
      cartMenu(state) {
        return state.serverData?.menu
      },
      cartGlobalMessage(state) {
        return state.serverData?.globalMessage?.message ?? ''
      },
      cartWarnings(state) {
        return state.serverData?.warnings ?? []
      },
      cartIsUberDirect(state) {
        return !!state.serverData?.isUberDirect
      },
      cartShippingCoordinates(state) {
        const { lat, lng } = state.serverData?.shippingAddress
        if (lat && lng) {
          return { lat, lng }
        }
        return null
      },
		},
		mutations: {
			setData: generateSetChild("serverData"),
			addItem: generateAddArrayChild("items"),
			updateItem: generateUpdateArrayChild("items"),
			removeItem: generateRemoveArrayChild("items"),
			setBasketToken: generateSetChild("basketToken"),
			setBasketChannel: generateSetChild("basketChannel"),
			setLoading: generateSetChild("loading"),
			setOpenRequests: generateSetChild("openRequests"),
			setChangedItems: generateSetChild("changedItems"),
			setUpdatedAt: generateSetChild("updatedAt"),
			setTip: generateSetChild('tip'),
			setTipType: generateSetChild('tipType'),
			setTipPercent: generateSetChild('tipPercent'),
			setTipOption: generateSetChild('tipOption'),
			setTipMainVariant: generateSetChild('tipMainVariant'),
			setTipSubVariant: generateSetChild('tipSubVariant'),
			setCo2Compensation: generateSetChild('co2Compensation'),
			setProduct: generateSetMapChild('productMap', 'code'),
			setProducts (state, { items }) {
				items.forEach(v => state.productMap[v.code] = v)
			},
			setShowContinue: generateSetChild('showContinue'),
			setIsDetached: generateSetChild('isDetached'),
			setIsFeedback: generateSetChild('isFeedback'),
			setCrossSell: generateSetChild('crossSell'),
		}
	}
}
